import { EventFragment, EventType } from "~/__generated__/graphql/graphql";

const SITE_ORIGIN = "https://app.joinbutter.co";
const ORG_FALLBACK_URL = "https://joinbutter.co";

type Availability = "InStock" | "SoldOut" | "PreOrder" | "Discontinued";

type LdJsonValue =
  | string
  | number
  | boolean
  | LdJsonObject
  | Array<LdJsonValue>;

interface LdJsonObject {
  [key: string]: LdJsonValue | undefined;
}

function priceFromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function availabilityFor(plan: EventFragment): Availability {
  if (plan.canceled) return "Discontinued";
  // edges are capped at first: 1000 — prefer pageInfo.totalSize when available.
  const attendeeCount =
    plan.attendees.pageInfo?.totalSize ?? plan.attendees.edges.length;
  const atCapacity = attendeeCount >= plan.capacity;
  if (!atCapacity) return "InStock";
  return plan.waitlists ? "PreOrder" : "SoldOut";
}

function pickImage(plan: EventFragment): string | undefined {
  const banners = (plan.bannerImageUrls ?? []).filter((u) => u);
  if (banners.length > 0) return banners[0];
  if (plan.bannerImageUrl) return plan.bannerImageUrl;
  if (plan.club?.imageUrl) return plan.club.imageUrl;
  const primaryHost = plan.hosts.find((h) => h.primary)?.user;
  if (primaryHost?.profileImageUrl) return primaryHost.profileImageUrl;
  return undefined;
}

function buildAddress(plan: EventFragment): LdJsonObject {
  const address: LdJsonObject = {
    "@type": "PostalAddress",
    addressCountry: "AU",
  };

  if (!plan.location.exactDetailsHidden && plan.location.formattedAddress) {
    address.streetAddress = plan.location.formattedAddress;
  }

  const locality = plan.location.suburb || plan.location.city;
  if (locality) address.addressLocality = locality;
  if (plan.location.state) address.addressRegion = plan.location.state;
  if (
    !plan.location.exactDetailsHidden &&
    plan.location.postcode &&
    plan.location.postcode > 0
  ) {
    address.postalCode = String(plan.location.postcode);
  }

  return address;
}

function buildLocation(plan: EventFragment): LdJsonObject {
  return {
    "@type": "Place",
    name: plan.location.exactDetailsHidden
      ? "Private location"
      : plan.location.name,
    address: buildAddress(plan),
  };
}

function buildOrganizer(plan: EventFragment): LdJsonObject {
  if (plan.club) {
    return {
      "@type": "Organization",
      name: plan.club.name,
      url: `${SITE_ORIGIN}/clubs/${plan.club.id}`,
    };
  }
  const primaryHost = plan.hosts.find((h) => h.primary)?.user;
  if (primaryHost) {
    return {
      "@type": "Organization",
      name: primaryHost.name,
      url: ORG_FALLBACK_URL,
    };
  }
  return {
    "@type": "Organization",
    name: "Butter",
    url: ORG_FALLBACK_URL,
  };
}

function priceForOffer(plan: EventFragment): string | null {
  if (plan.type !== EventType.Ticketed) return "0";
  // Ticketed: prefer the regular tier; fall back to early bird if that's
  // the only tier configured. If neither has data, return null so the
  // caller can omit the offer rather than mis-advertise it as free.
  const cents =
    plan.regularTicketCost?.totalCostCents ??
    plan.earlyBirdTicketCost?.totalCostCents ??
    null;
  return cents == null ? null : priceFromCents(cents);
}

function buildOffers(
  plan: EventFragment,
  eventUrl: string,
): LdJsonObject | undefined {
  const price = priceForOffer(plan);
  if (price == null) return undefined;

  const offers: LdJsonObject = {
    "@type": "Offer",
    url: eventUrl,
    price,
    priceCurrency: "AUD",
    availability: `https://schema.org/${availabilityFor(plan)}`,
  };

  if (plan.publishedAt) {
    offers.validFrom = String(plan.publishedAt);
  }

  return offers;
}

/**
 * Build the Schema.org Event JSON-LD object for a plan page, per BUT-1726.
 * Returns null if mandatory fields (name, startDate, location) are missing.
 */
export function buildEventJsonLd(
  plan: EventFragment,
  eventUrl: string,
): LdJsonObject | null {
  if (!plan.title || !plan.startDate) return null;

  const primaryHost = plan.hosts.find((h) => h.primary)?.user;
  const image = pickImage(plan);

  const ld: LdJsonObject = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: plan.title,
    description: plan.description?.trim() || plan.headline?.trim() || plan.title,
    url: eventUrl,
    startDate: String(plan.startDate),
    eventStatus: plan.canceled
      ? "https://schema.org/EventCancelled"
      : "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: buildLocation(plan),
    organizer: buildOrganizer(plan),
  };

  const offers = buildOffers(plan, eventUrl);
  if (offers) ld.offers = offers;

  // Omit endDate when absent or when it doesn't extend past startDate
  // (DB defaults endTime to startTime when "no end time" is set).
  if (plan.endDate && String(plan.endDate) > String(plan.startDate)) {
    ld.endDate = String(plan.endDate);
  }

  if (image) ld.image = image;

  if (primaryHost) {
    ld.performer = {
      "@type": "Person",
      name: primaryHost.name,
    };
  }

  return ld;
}
